import { useId } from "react";
import { Check } from "lucide-react";
import {
  COMPANY_PLAN_DETAILS,
  DRIVER_PLAN_DETAILS,
} from "@ridereserv/shared/constants";
import { cn } from "@ridereserv/shared/utils";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { LinkButton } from "@/components/ui/LinkButton";
import type { Audience } from "./AudienceValueSection";

/**
 * Normalized shape we render in a card, derived from the canonical
 * `*_PLAN_DETAILS` constants so prices/features/names never drift from the
 * drift-tested source of truth in `@ridereserv/shared/constants`.
 */
interface PricingPlan {
  id: string;
  name: string;
  price: number;
  features: readonly string[];
  /** CTA destination — companies and drivers register through distinct flows. */
  href: string;
  /** Highlight as the recommended plan (cobalt ring + "Most popular" badge). */
  popular: boolean;
}

// Driver funnel. Both seeded driver tiers, cheapest first. Drivers register via
// the driver flow.
const driverPlans: PricingPlan[] = (
  [DRIVER_PLAN_DETAILS.free_driver, DRIVER_PLAN_DETAILS.paid_driver] as const
).map((plan) => ({
  id: plan.id,
  name: plan.name,
  price: plan.price,
  features: plan.features,
  href: "/register?type=driver",
  // Drivers have no "most popular" tier — the upgrade story is just free→paid.
  popular: false,
}));

// Company funnel, in deliberate value order: business → business_plus →
// business_ultimate (never "Enterprise"). business_plus is the anchor tier.
const companyPlans: PricingPlan[] = (
  [
    COMPANY_PLAN_DETAILS.business,
    COMPANY_PLAN_DETAILS.business_plus,
    COMPANY_PLAN_DETAILS.business_ultimate,
  ] as const
).map((plan) => ({
  id: plan.id,
  name: plan.name,
  price: plan.price,
  features: plan.features,
  href: "/register?type=business",
  popular: plan.id === "business_plus",
}));

interface AudienceCopy {
  /** One-line reframe of the offer for this audience, shown under the heading. */
  blurb: string;
  cta: string;
  plans: PricingPlan[];
}

// Keyed by audience so `AUDIENCES[audience]` is always defined (no optional
// `.find()` lookup).
const AUDIENCES: Record<Audience, AudienceCopy> = {
  drivers: {
    blurb:
      "Run your own book of business. Start free, upgrade when the rides come in.",
    cta: "Start driving",
    plans: driverPlans,
  },
  companies: {
    blurb:
      "Dispatch a whole fleet from one console. Scale drivers, branding, and automation.",
    cta: "Get started",
    plans: companyPlans,
  },
};

/**
 * Render a single plan's price as a ledger figure: large mono tabular numerals
 * with a `/mo` micro-label. $0 plans read "$0".
 * @param root0 - Component props
 * @param root0.price - Monthly price in whole dollars
 * @returns The formatted price block
 */
function PriceTag({ price }: { price: number }) {
  return (
    <div className="flex items-baseline gap-1">
      <span className="font-mono text-4xl font-semibold tabular-nums tracking-tight text-neutral-900">
        ${price}
      </span>
      <span className="font-mono text-sm tabular-nums text-neutral-500">
        /mo
      </span>
    </div>
  );
}

/**
 * A single pricing card. The recommended plan lifts via a cobalt ring + tinted
 * surface + top accent bar (flat — no drop shadow, per the design system).
 * @param root0 - Component props
 * @param root0.plan - The normalized plan to render
 * @param root0.cta - CTA button label for this audience
 * @returns The pricing card
 */
function PlanCard({ plan, cta }: { plan: PricingPlan; cta: string }) {
  return (
    <Card
      className={cn(
        "relative flex flex-col overflow-hidden transition-colors",
        plan.popular
          ? "border-primary-300 bg-primary-50/40 ring-1 ring-primary-500"
          : "hover:border-neutral-300"
      )}
    >
      {/* Top accent bar marks the anchor tier without any shadow. */}
      {plan.popular && (
        <div className="h-1 w-full bg-primary-600" aria-hidden="true" />
      )}
      <CardHeader className="gap-3 p-5 pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-neutral-600">
            {plan.name}
          </CardTitle>
          {plan.popular && <Badge variant="default">Most popular</Badge>}
        </div>
        <PriceTag price={plan.price} />
      </CardHeader>
      <CardContent className="flex-1 p-5 pt-1">
        <ul className="space-y-2.5">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2.5">
              <Check
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-600"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              <span className="text-sm leading-snug text-neutral-700">
                {feature}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter className="p-5 pt-3">
        <LinkButton
          to={plan.href}
          variant={plan.popular ? "primary" : "outline"}
          className="w-full"
        >
          {cta}
        </LinkButton>
      </CardFooter>
    </Card>
  );
}

interface PricingTeaserSectionProps {
  /**
   * The audience whose plans to show — controlled by the page-level audience
   * toggle so pricing always matches the value section and FAQ above it.
   */
  audience: Audience;
}

/**
 * Homepage pricing section. Renders one audience's plan ladder — independent
 * drivers OR transportation companies — driven entirely by the canonical
 * `*_PLAN_DETAILS` constants (no hardcoded prices/features). The audience is
 * controlled by the page-level toggle, so this section has no toggle of its own.
 *
 * Presentational and self-contained: no data fetching, no auth. The integration
 * unit wraps this with `id="pricing"`.
 * @param root0 - Component props
 * @param root0.audience - The audience whose plans to render
 * @returns The pricing teaser section
 */
export function PricingTeaserSection({ audience }: PricingTeaserSectionProps) {
  const headingId = useId();
  const active = AUDIENCES[audience];

  return (
    <section
      aria-labelledby={headingId}
      className="bg-background px-4 py-16 sm:px-6 lg:px-8 lg:py-20"
    >
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col items-center text-center">
          <span className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
            Pricing
          </span>
          <h2
            id={headingId}
            className="mt-3 text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl"
          >
            One platform. Two ways to grow.
          </h2>
          <p className="mt-3 max-w-xl text-sm text-neutral-600 sm:text-base">
            {active.blurb}
          </p>
        </div>

        <div
          className={cn(
            "mt-10 grid gap-5",
            audience === "drivers"
              ? "sm:grid-cols-2 lg:mx-auto lg:max-w-3xl"
              : "sm:grid-cols-2 lg:grid-cols-3"
          )}
        >
          {active.plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} cta={active.cta} />
          ))}
        </div>
      </div>
    </section>
  );
}
